import React, { FC, Fragment, useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import DenthubLogo from 'components/navigation/app-bar/logo/denthub-logo.component';
import Search from 'components/navigation/app-bar/search/search.component';
import Notifications from 'components/navigation/app-bar/notifications/notifications.component';
import AccountMenu from 'components/navigation/app-bar/account-menu/account-menu.component';
import { Box, Chip, Hidden, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WhitelabelLogo from 'components/navigation/app-bar/logo/whitelabel-logo.component';
import DrawerMenuContainer from 'components/navigation/drawer-menu/drawer-menu.container';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { permissions } from 'utils/permission';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { ConnectedLab } from 'models/organization';
import Cookies from 'js-cookie';
import ScannerCaseNotifications from './scanner-case-notifications/scanner-case-notifications.component';
import { BusinessOutlined } from '@mui/icons-material';

const { LAB_ID_KEY } = CONSTANTS;

const MainAppBar: FC = () => {
    const { t: translate } = useTranslation('navigation');
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [whitelabelLogo, setWhitelabelLogo] = useState<string | null>(null);
    const isScannerCasesEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.SCAN_CASES,
    );

    useEffect(() => {
        // Handle whitelabel style overrides
        if (permissions.isWhiteLabeledOrganization(currentOrganization) && currentOrganization?.connectedLabs?.length) {
            const currentLabId = Cookies.get(LAB_ID_KEY);
            const lab = currentOrganization.connectedLabs.find(
                (cLab: ConnectedLab) => cLab.lab._id === currentLabId,
            )?.lab;
            if (lab) {
                setWhitelabelLogo(lab.logoSignedUrl);
                const bgColor = lab.hexCode || '#093459';
                const textColor = lab.textCode || '#fff';
                const style = document.createElement('style');
                style.textContent = `
                    .wl-text {color: ${textColor} !important;}
                    .wl-background {background: ${bgColor} !important;}
                    .wl-search-input {color: #111 !important; background: white !important; border: 1px ${textColor} solid;}
                `;
                document.head.appendChild(style);
            }
        }
    }, []);

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    return (
        <Fragment>
            <AppBar
                sx={(theme) => ({
                    zIndex: theme.zIndex.drawer + 1,
                })}
                position="fixed"
                className="wl-background"
            >
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        className="wl-text"
                        size="large"
                        sx={(theme) => ({
                            color: '#fff',
                            marginRight: theme.spacing(1),
                            marginLeft: `calc(1em + ${theme.spacing(5.5)} * -1)`,
                            [theme.breakpoints.down('sm')]: {
                                marginLeft: `calc(1em + ${theme.spacing(4)} * -1)`,
                            },
                        })}
                        data-test="main-app-bar.menu-button"
                    >
                        <MenuIcon />
                    </IconButton>
                    {whitelabelLogo ? <WhitelabelLogo logo={whitelabelLogo} /> : <DenthubLogo />}
                    {currentOrganization?.name && (
                        <Hidden smDown>
                            <Tooltip title={translate('CURRENT_ORGANIZATION')} arrow>
                                <Chip
                                    className="wl-text"
                                    variant="outlined"
                                    icon={<BusinessOutlined />}
                                    label={currentOrganization.name}
                                    sx={{
                                        color: 'inherit',
                                        background: 'rgba(255,255,255,0.15)',
                                        borderColor: 'inherit',
                                        backdropFilter: 'blur(8px)',
                                        boxShadow: '0 0 8px rgba(255,255,255,0.2)',
                                        fontWeight: 800,
                                        mr: 1.5,
                                        '& .MuiChip-icon': { marginLeft: '8px', fontSize: '15px', color: 'inherit' },
                                    }}
                                />
                            </Tooltip>
                        </Hidden>
                    )}
                    {!permissions.isExternalTechnician(currentUser.role) && !permissions.isCarrier(currentUser.role) ? (
                        <Fragment>
                            <Hidden smDown>
                                <Box mr={1.5}>
                                    <Search />
                                </Box>
                            </Hidden>
                        </Fragment>
                    ) : undefined}
                    {isScannerCasesEnabled &&
                    permissions.isLabTechnician(currentUser.type) &&
                    !permissions.isExternalTechnician(currentUser.role) &&
                    !permissions.isCarrier(currentUser.role) ? (
                        <ScannerCaseNotifications />
                    ) : undefined}
                    <Notifications />
                    <AccountMenu />
                </Toolbar>
            </AppBar>
            <DrawerMenuContainer
                isOpen={drawerOpen}
                onNavigation={() => {
                    setDrawerOpen(false);
                }}
            />
        </Fragment>
    );
};

export default MainAppBar;
